import React, { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { track } from "@/lib/track";
import { HOME } from "@/constants/testIds";

const PensamentoContinuo = ({ eyebrow = "III · o pensamento", title = "Artigos.", openSlugInitial = null }) => {
    const { data, isLoading } = useQuery({
        queryKey: ["texts"],
        queryFn: async () => (await api.get("/texts")).data,
    });
    const [openSlug, setOpenSlug] = useState(openSlugInitial);
    // Timestamp do momento em que o artigo aberto foi expandido. Usado para
    // calcular o tempo real de leitura no fechamento (ou no unload).
    const openedAt = useRef(openSlugInitial ? Date.now() : null);

    const closeCurrent = (nextSlug = null) => {
        if (openSlug && openedAt.current) {
            const dwell_s = Math.round((Date.now() - openedAt.current) / 1000);
            // Só reporta se leu por >2s — evita ruído de cliques acidentais
            if (dwell_s >= 2) {
                track("article_dwell", { slug: openSlug, dwell_s });
            }
        }
        openedAt.current = nextSlug ? Date.now() : null;
        setOpenSlug(nextSlug);
    };

    // Dispara o dwell também se a aba for fechada com um artigo aberto.
    useEffect(() => {
        const onHide = () => {
            if (openSlug && openedAt.current) {
                const dwell_s = Math.round((Date.now() - openedAt.current) / 1000);
                if (dwell_s >= 2) {
                    track("article_dwell", { slug: openSlug, dwell_s });
                }
            }
        };
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") onHide();
        });
        window.addEventListener("pagehide", onHide);
        return () => {
            window.removeEventListener("pagehide", onHide);
        };
    }, [openSlug]);

    return (
        <section
            data-testid={HOME.pensamentoSection}
            className="relative bg-[#0E2018] border-t border-[#1B3025]"
            aria-label="Pensamento contínuo"
        >
            <div className="max-w-3xl mx-auto px-6 py-24 md:py-32">
                <header className="mb-14 md:mb-20">
                    <p className="text-[10px] uppercase tracking-[0.32em] text-[#7E8978] font-sans font-light mb-4">
                        {eyebrow}
                    </p>
                    <h2 className="font-serif font-light text-[34px] md:text-[40px] leading-[1.15] text-[#F2EFE5] max-w-2xl text-balance">
                        {title}
                    </h2>
                </header>

                {isLoading && (
                    <p className="font-sans font-light text-[13px] text-[#7E8978]">
                        Carregando textos…
                    </p>
                )}

                {/* Separa itens por registro: ensaios primeiro (leitura na
                    tela, voz da casa), depois artigos acadêmicos (papers,
                    com link de leitura completa no SSRN quando houver). */}
                {(() => {
                    const essays = (data || []).filter((t) => (t.kind || "essay") !== "paper");
                    const papers = (data || []).filter((t) => t.kind === "paper");
                    const renderItem = (t) => {
                        const isOpen = openSlug === t.slug;
                        const isPaper = t.kind === "paper";
                        return (
                            <li
                                key={t.id}
                                id={t.slug}
                                data-testid={HOME.pensamentoItem(t.slug)}
                                className="border-b border-[#1B3025] py-10 first:pt-0 scroll-mt-24"
                            >
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!isOpen) {
                                            track("article_open", { slug: t.slug });
                                            closeCurrent(t.slug);
                                        } else {
                                            closeCurrent(null);
                                        }
                                    }}
                                    className="text-left w-full group"
                                    aria-expanded={isOpen}
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <p className="text-[10px] uppercase tracking-[0.22em] text-[#7E8978] font-sans font-light">
                                            {new Date(t.created_at).toLocaleDateString("pt-BR", {
                                                day: "2-digit", month: "long", year: "numeric",
                                            })}
                                        </p>
                                        {isPaper && (
                                            <span className="text-[9px] uppercase tracking-[0.24em] px-2 py-0.5 rounded-sm bg-[#c2761b]/15 text-[#c2761b] border border-[#c2761b]/30 font-sans font-light">
                                                Artigo acadêmico
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="font-serif font-light text-[22px] md:text-[26px] leading-snug text-[#EAEAE6] group-hover:text-white transition-colors">
                                        {t.title}
                                    </h3>
                                    {t.excerpt && (
                                        <p className="mt-3 font-sans font-light text-[13.5px] md:text-[14px] leading-relaxed text-[#B0B6A6] italic max-w-2xl">
                                            {t.excerpt}
                                        </p>
                                    )}
                                </button>
                                {isOpen && (
                                    <div className="mt-6 max-w-2xl flex flex-col gap-5 font-serif text-[16px] md:text-[18px] leading-[1.78] text-[#F2EFE5]">
                                        {t.body.split(/\n\s*\n/).map((block, bi) => {
                                            const trimmed = block.trim();
                                            if (!trimmed) return null;
                                            if (trimmed.startsWith("## ")) {
                                                return (
                                                    <div key={bi} className="mt-10 md:mt-14 pt-6">
                                                        <span aria-hidden="true" className="block w-8 h-px bg-[#234135] mb-5" />
                                                        <h3 className="font-serif font-light text-[22px] md:text-[26px] leading-snug text-[#F2EFE5] tracking-[-0.01em]">
                                                            {trimmed.slice(3)}
                                                        </h3>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <p key={bi} className="text-balance">
                                                    {trimmed}
                                                </p>
                                            );
                                        })}
                                        {isPaper && t.external_url && (
                                            <a
                                                href={t.external_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`ssrn-link-${t.slug}`}
                                                onClick={() => track("ssrn_click", { slug: t.slug })}
                                                className="mt-6 group inline-flex items-center gap-2.5 font-sans font-light text-[12px] uppercase tracking-[0.24em] text-[#c2761b] hover:text-[#e09947] transition-colors w-fit"
                                            >
                                                <span className="border-b border-[#c2761b]/50 group-hover:border-[#e09947] pb-0.5 transition-colors">
                                                    Ler o artigo completo no SSRN
                                                </span>
                                                <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">→</span>
                                            </a>
                                        )}
                                        {isPaper && !t.external_url && (
                                            <p className="mt-4 font-sans font-light text-[11.5px] italic text-[#7E8978]">
                                                Ensaio teórico em desenvolvimento — versão completa será publicada em repositório acadêmico.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </li>
                        );
                    };
                    return (
                        <>
                            {essays.length > 0 && (
                                <ul className="flex flex-col">
                                    {essays.map(renderItem)}
                                </ul>
                            )}
                            {papers.length > 0 && (
                                <>
                                    <div className="mt-16 md:mt-20 mb-10">
                                        <p className="text-[10px] uppercase tracking-[0.32em] text-[#7E8978] font-sans font-light mb-3">
                                            Artigos acadêmicos
                                        </p>
                                        <p className="font-serif font-light italic text-[14px] md:text-[15px] leading-[1.6] text-[#B0B6A6] max-w-xl">
                                            Ensaios teóricos publicados — versões completas no SSRN.
                                        </p>
                                    </div>
                                    <ul className="flex flex-col">
                                        {papers.map(renderItem)}
                                    </ul>
                                </>
                            )}
                            {data && data.length === 0 && (
                                <p className="font-sans font-light text-[13px] text-[#7E8978]">
                                    Em breve — textos serão acrescentados aqui, um por vez.
                                </p>
                            )}
                        </>
                    );
                })()}
            </div>
        </section>
    );
};

export default PensamentoContinuo;
